import { WindowLocation, useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { parse } from 'query-string';
import { and, not } from 'ramda';
import { useEffect, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

import { getPathPrefixFromPathname } from '@utils/flowHelpers';
import { exists } from '@utils/general';

import { useAuth } from '@providers/AuthProvider';

import { FlowIdTypes, FlowTypes, FormData } from '@folx-types/flow';

import useFlowData from './useFlowData';
import { useMemberValidation } from './useMemberValidation';

type HookParams = {
	flowId: FlowTypes | FlowIdTypes;
	flowPath: string;
	firstStepPath: string;
	currentStepKey: string;
	isSecureRoute: boolean;
	onboardedFlowName: string;
	isOnboardingStep?: boolean;
	customReroute?: string;
	customReturn?: string;
};

type UseOnboardedRoute = (params: HookParams) => {
	formData: FormData;
	loaded: boolean;
	location: WindowLocation<unknown>;
	loggedIn: boolean;
	submitFormData: (data: FormData) => Promise<void>;
};

const useOnboardedRoute: UseOnboardedRoute = ({
	flowId,
	flowPath,
	firstStepPath,
	currentStepKey,
	isOnboardingStep,
	isSecureRoute,
	customReroute,
	customReturn,
}) => {
	const [loaded, setLoaded] = useState(false);
	const [routed, setRouted] = useState(false);
	const location = useLocation();

	const { loaded: loadedForms, formData, submitFormData } = useFlowData(flowId);

	const { loggedIn, initialized: authInitialized } = useAuth();

	const { loaded: memberValidationLoaded, validateMember, memberMetadata } = useMemberValidation();

	const navigateToMembership = () => {
		navigate(`/app/flows/membership`, {
			replace: true,
		});
	};

	useEffect(() => {
		if (authInitialized && loadedForms && memberValidationLoaded) {
			setLoaded(true);
		}
	}, [authInitialized, loadedForms, memberValidationLoaded]);

	useDeepCompareEffect(() => {
		if (!loaded) {
			return;
		}

		if (!loggedIn) {
			navigate('/app/sign_in', {
				state: {
					redirectTo: (location?.state?.redirectTo || location?.pathname) ?? '/',
				},
			});
			return;
		}

		const isOnboardingIncomplete = not(memberMetadata.account.isOnboardingComplete);
		const isMembershipInactive = memberMetadata.membership.isMembershipInactive;

		validateMember({ isProductFlow: true });

		const shouldReroute = and(isOnboardingIncomplete, isMembershipInactive);

		if (!currentStepKey) {
			const pathPrefix = getPathPrefixFromPathname(location.pathname, flowPath);
			const path = `/${pathPrefix}/${flowPath ? `${flowPath}/` : ''}${firstStepPath}`;
			navigate(path, { replace: true });
			return;
		}

		if (!shouldReroute || isOnboardingStep) {
			setRouted(true);
			return;
		}

		if (exists(customReroute)) {
			navigate(customReroute, { state: { onReturn: customReturn } });
			return;
		}

		exists(currentStepKey) && navigateToMembership();
		setRouted(true);
	}, [
		isOnboardingStep,
		isSecureRoute,
		loggedIn,
		authInitialized,
		location.pathname,
		currentStepKey,
		firstStepPath,
		flowPath,
		loaded,
	]);

	useEffect(() => {
		const urlParams = parse(location.search);
		if (formData && urlParams?.isConsult) {
			formData.isGahtConsult = true;
		}
	}, [location.search, formData]);

	return {
		loaded: loaded && routed,
		loggedIn,
		formData,
		submitFormData,
		location,
	};
};

export default useOnboardedRoute;
